/**
 * Unapproved Staff Email Exception Config
 *
 * Covers: ex-user-ls-unapproved-domain
 *
 * Active Lightspeed users whose email sits on a domain not in the
 * approved list (stored on the exception type's function_params JSON).
 * Highlights the offending email domain; shows MFA and primary-user
 * flags so HQ can quickly triage high-risk accounts.
 */
import React from 'react'
import { Box, Chip, Stack, Typography } from '@mui/material'
import {
  Person as PersonIcon,
  Star as PrimaryIcon,
} from '@mui/icons-material'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import { composeRowSubtitle, textPart } from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { INVALID_CELL_BG } from '../../../theme/exceptionTableStyles'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'

const domainCellStyle = (): Record<string, any> => ({
  backgroundColor: INVALID_CELL_BG,
})

const renderStaff = (value: any, row: Record<string, any>): React.ReactNode =>
  React.createElement(
    Box,
    { sx: { display: 'flex', alignItems: 'center', gap: 1 } },
    React.createElement(PersonIcon, { color: 'action', fontSize: 'small' }),
    React.createElement(Typography, { variant: 'body2', fontWeight: 'medium' }, value),
    row.is_primary_user &&
      React.createElement(PrimaryIcon, {
        fontSize: 'small',
        sx: { color: 'warning.main' },
      })
  )

const renderDomain = (value: any): React.ReactNode =>
  React.createElement(
    Typography,
    { variant: 'body2', color: 'error', fontWeight: 'medium' },
    value || '-'
  )

const renderMfa = (value: any): React.ReactNode =>
  React.createElement(
    Chip,
    statusPillChipProps(value ? 'On' : 'Off', value ? 'success' : 'warning'),
  )

const extractRoleNames = (value: any): string[] => {
  if (value == null || value === '') return []
  if (Array.isArray(value)) {
    return value
      .map((r) => {
        if (r && typeof r === 'object' && 'name' in r) return String((r as any).name).trim()
        return String(r).trim()
      })
      .filter(Boolean)
  }
  if (typeof value === 'object' && value !== null && 'name' in value) {
    return [String((value as any).name).trim()].filter(Boolean)
  }
  const str = String(value)
  const nameMatches = [...str.matchAll(/['"]name['"]\s*:\s*['"]([^'"]+)['"]/g)]
  if (nameMatches.length > 0) {
    return nameMatches.map((m) => m[1].trim()).filter(Boolean)
  }
  return str
    .split(/[,;\n]/)
    .map((r) => r.trim())
    .filter(Boolean)
}

const renderRoles = (value: any): React.ReactNode => {
  const roles = extractRoleNames(value)
  if (roles.length === 0) return '-'
  return React.createElement(
    Stack,
    { spacing: 0.25 },
    ...roles.map((role, idx) =>
      React.createElement(
        Typography,
        { key: idx, variant: 'body2' },
        role,
      ),
    ),
  )
}

const formatSeenAt = (value: any): React.ReactNode => {
  if (!value) return '-'
  const date = new Date(value)
  return date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'display_name',
    label: 'User',
    minWidth: 220,
    contexts: ['inspector'],
    render: renderStaff,
  },
  {
    key: 'email',
    label: 'Email',
    contexts: ['inspector'],
  },
  {
    key: 'email_domain',
    label: 'Domain',
    contexts: ['inspector'],
    render: renderDomain,
    getCellStyle: domainCellStyle,
  },
  {
    key: 'roles',
    label: 'Roles',
    contexts: ['inspector'],
    render: renderRoles,
  },
  {
    key: 'restricted_outlet_name',
    label: 'Outlet',
    contexts: ['inspector'],
    render: (value) => value || 'All outlets',
  },
  {
    key: 'seen_at',
    label: 'Last Seen',
    align: 'center',
    contexts: ['inspector'],
    render: formatSeenAt,
    getSortValue: (row) => row.seen_at || '',
  },
  {
    key: 'enabled_mfa_otp',
    label: 'MFA',
    align: 'center',
    contexts: ['inspector'],
    render: renderMfa,
    getSortValue: (row) => (row.enabled_mfa_otp ? 1 : 0),
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'display_name',
    label: 'User',
    minWidth: 220,
    contexts: ['platform'],
  },
  {
    key: 'email',
    label: 'Email',
    contexts: ['platform'],
  },
  {
    key: 'email_domain',
    label: 'Domain',
    contexts: ['platform'],
  },
  {
    key: 'roles',
    label: 'Roles',
    contexts: ['platform'],
  },
]

export const unapprovedStaffEmailConfig: ExceptionConfig = {
  codes: ['ex-user-ls-unapproved-domain'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'display_name',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowTitle: (row) =>
    textPart(row.display_name) || textPart(row.username) || textPart(row.email) || 'Staff member',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      textPart(row.email),
      textPart(row.roles) && `role: ${textPart(row.roles)}`,
      textPart(row.outlet_name),
    ]),
}
